import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  BacklogConfigResponse,
  BacklogMember,
  BacklogProjectMetaResponse,
  BacklogTaskOverride,
  BacklogTaskResult,
  CreateBacklogTasksResponse,
  MeetingMinutesActionItem,
  UpdateBacklogIssuePayload,
  UpdateBacklogIssueResponse,
} from '@ai-meeting/shared'
import {
  fetchBacklogConfig,
  fetchBacklogMembers,
  fetchBacklogProjectMeta,
  syncBacklogTasks,
  updateBacklogIssue,
} from '@/services/backlog'
import { getServiceStrings } from '@/services/i18n'
import { logActivity } from '@/services/activity'
import { parseDirectoryEmail } from '@/utils/directoryValue'
import { isAlreadyCreated, needsConfirmation } from '@/pages/Meetings/utils/backlogConfirmDraft'

export type BacklogSyncPhase = 'idle' | 'checking' | 'syncing'

export interface BacklogSyncState {
  /** null while loading, and for good once the space turns out not to be configured. */
  config: BacklogConfigResponse | null
  members: BacklogMember[] | null
  /** True while the roster for the current project is in flight. */
  membersLoading: boolean
  /** Roster read failure; null while idle or after a successful load. */
  membersError: string | null
  /** Re-run the roster read for the current project (Retry on the assignee picker). */
  reloadMembers: () => void
  /** Verdict per action-item index, from the check or from the last write. */
  rows: Map<number, BacklogTaskResult>
  /**
   * Who each row will be assigned to, per action-item index. A row with no
   * entry is left for the server to decide.
   */
  assigneeIds: Record<number, number | null>
  phase: BacklogSyncPhase
  error: string | null
  outcome: CreateBacklogTasksResponse | null
  /**
   * Action-item indexes the reviewer unticked, which are left out of the write
   * entirely (BRAI-575).
   *
   * Not the same as `status: 'skipped_duplicate'` on a row — that is the server
   * finding the item already on Backlog. This is somebody deciding it should
   * not go.
   */
  skipped: Set<number>
  toggleSkip: (index: number) => void
  sync: () => Promise<void>
  dismiss: () => void
  /** Priority names this project accepts; empty until read, or if it cannot be. */
  priorities: string[]
  /**
   * Issue type names this project accepts, which is all this hook owns of the
   * column: the chosen value lives on the saved action item, because the push
   * reads the stored minutes rather than the screen. Empty until read, or if
   * it cannot be — the column then shows the default and offers no picker.
   */
  issueTypes: string[]
  /**
   * How many rows the batch would actually create.
   *
   * Zero means pressing Push has nothing to do: everything left is either
   * unticked or an overwrite, and an overwrite is only ever written from its
   * own panel.
   */
  creatableCount: number
  /**
   * Rows whose confirmation panel the reviewer has folded away.
   *
   * Every matched row opens its panel on arrival — the row already carries
   * everything the panel shows, so there is nothing to wait for and no reason
   * to make somebody hunt for a button. This is the opt-out, not the opt-in.
   */
  collapsedConfirm: Set<number>
  toggleConfirm: (index: number) => void
  /** Per row, because every matched row has a panel open at once. */
  confirmError: Map<number, string>
  confirmNote: Map<number, 'noop'>
  confirmApplying: Set<number>
  applyConfirm: (index: number, payload: UpdateBacklogIssuePayload) => Promise<void>
}

interface PendingConfirmedAction {
  item: MeetingMinutesActionItem
  result: UpdateBacklogIssueResponse
}

/** Identity of the rows a verdict was computed for; a change invalidates it. */
function itemsKey(items: MeetingMinutesActionItem[]): string {
  return JSON.stringify(items.map((item) => [item.content, item.assignee, item.deadline]))
}

/**
 * Convert the full value approved in the Backlog confirmation panel back into
 * the fields Meeting Minutes owns. The payload is sparse, so unchanged values
 * come from the ticket the reviewer confirmed rather than from the stale local
 * action item.
 */
function confirmedActionItem(
  item: MeetingMinutesActionItem,
  row: BacklogTaskResult,
  payload: UpdateBacklogIssuePayload,
  members: BacklogMember[] | null,
): MeetingMinutesActionItem {
  const content =
    payload.summary !== undefined
      ? payload.summary?.trim() || row.current_summary || item.content
      : row.current_summary || item.content
  const assigneeWasExplicitlyConfirmed = payload.assignee_id !== undefined
  const assigneeId = assigneeWasExplicitlyConfirmed
    ? payload.assignee_id
    : (row.current_assignee_id ?? item.assignee_id ?? null)
  const memberName = members?.find((member) => member.id === assigneeId)?.name
  const assignee =
    !assigneeWasExplicitlyConfirmed && row.current_assignee_id === null
      ? item.assignee
      : assigneeId === null
        ? ''
        : (memberName ??
          (assigneeId === row.current_assignee_id ? row.current_assignee_name : null) ??
          (assigneeId === item.assignee_id ? item.assignee : ''))
  const deadline =
    payload.due_date !== undefined ? (payload.due_date ?? '') : (row.current_deadline ?? '')

  return {
    ...item,
    content,
    assignee,
    assignee_id: assigneeId,
    deadline,
  }
}

/**
 * Keep the minutes' action items lined up against Backlog.
 *
 * Checks on its own as soon as the screen opens, because the table has to say
 * "update ticket" or "create new" before the reviewer decides anything — making
 * them press a button to find out would be the same as making them press Sync
 * to find out. The check is the same call as the write with `dry_run` on, so
 * the verdict shown is the one the write will act on; a copy of the matching
 * rules here would disagree with the server sooner or later.
 *
 * @param enabled Skip everything while the minutes are not ready or not the
 *   caller's to sync.
 * @param connected Whether the reader has their own Backlog key stored. Every
 *   Backlog-touching call now carries it (BRAI-537), so without one the check
 *   and the member list would only come back 403 — the space config is still
 *   read, because that is what tells the screen to offer connecting at all.
 * @param projectKey The project the picker roster / meta follow — the unsaved
 *   draft pick while editing, otherwise the saved meeting project.
 * @param writeProjectKey The project Push/dry-run still target (the stored
 *   meeting). When it differs from {@link projectKey}, email-matched ids from
 *   the draft roster must not travel as write overrides.
 * @param checkEnabled When false, still load config/members/meta for the
 *   draft project picker, but do not dry-run against the stored meeting.
 */
export function useBacklogSync({
  meetingId,
  actionItems,
  enabled = true,
  connected = false,
  projectKey = null,
  writeProjectKey = null,
  checkEnabled = true,
  onConfirmedActionPersist,
}: {
  meetingId: string
  actionItems: MeetingMinutesActionItem[]
  enabled?: boolean
  connected?: boolean
  projectKey?: string | null
  writeProjectKey?: string | null
  checkEnabled?: boolean
  /** Persist the full confirmed values back into Meeting Minutes. */
  onConfirmedActionPersist?: (index: number, item: MeetingMinutesActionItem) => Promise<void>
}): BacklogSyncState {
  const [config, setConfig] = useState<BacklogConfigResponse | null>(null)
  const [members, setMembers] = useState<BacklogMember[] | null>(null)
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)
  const [membersReloadToken, setMembersReloadToken] = useState(0)
  const [rows, setRows] = useState<Map<number, BacklogTaskResult>>(new Map())
  const [phase, setPhase] = useState<BacklogSyncPhase>('idle')
  const [error, setError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<CreateBacklogTasksResponse | null>(null)
  const [skipped, setSkipped] = useState<Set<number>>(() => new Set())
  const [meta, setMeta] = useState<BacklogProjectMetaResponse | null>(null)
  const [collapsedConfirm, setCollapsedConfirm] = useState<Set<number>>(() => new Set())
  const [confirmError, setConfirmError] = useState<Map<number, string>>(() => new Map())
  const [confirmNote, setConfirmNote] = useState<Map<number, 'noop'>>(() => new Map())
  const [confirmApplying, setConfirmApplying] = useState<Set<number>>(() => new Set())
  // An external Backlog write cannot be rolled back with the meeting DB write.
  // Keep the latter so Retry never sends the already-successful overwrite again.
  const pendingConfirmedActionsRef = useRef<Map<number, PendingConfirmedAction>>(new Map())
  // Persisting the confirmed values changes `actionItems`, but that snapshot
  // was just checked and written. Suppress only that exact follow-up key so
  // the settled row does not immediately reopen from a redundant dry run.
  const confirmedItemsKeyRef = useRef<string | null>(null)

  /**
   * Config / roster / project-meta follow the project picker even when the
   * meeting still has zero action items — otherwise choosing a project never
   * calls GET /members until rows exist on the *saved* minutes.
   */
  const rosterActive = enabled
  /** Dry-run / write path: need rows on the meeting the server will read. */
  const active = enabled && actionItems.length > 0
  /**
   * Identity of the rows to check. A parent re-render hands back a fresh array
   * with the same contents, and keying the effect on the contents rather than
   * on the array is what stops that from costing a Backlog round trip.
   */
  const key = itemsKey(actionItems)

  const reloadMembers = useCallback(() => {
    setMembersReloadToken((token) => token + 1)
  }, [])

  useEffect(() => {
    if (!rosterActive) return undefined
    let cancelled = false
    void fetchBacklogConfig().then((loaded) => {
      if (!cancelled && loaded) setConfig(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [rosterActive])

  // Split from the config read above rather than nested inside it: the member
  // list is read with the reader's own key, which may only arrive after the
  // config has, and a nested fetch would never be retried when it does.
  // Alongside the member list and for the same reason: both fill a picker in
  // the confirmation panel, and both are read with the caller's own grant.
  useEffect(() => {
    if (!rosterActive || !connected) return undefined
    let cancelled = false
    void fetchBacklogProjectMeta(projectKey).then((loaded) => {
      if (!cancelled) setMeta(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [rosterActive, connected, projectKey])

  const canAssign = Boolean(config?.assign_enabled)
  useEffect(() => {
    if (!rosterActive || !connected || !canAssign) {
      setMembers(null)
      setMembersLoading(false)
      setMembersError(null)
      return undefined
    }
    let cancelled = false
    const requestedKey = projectKey?.trim() || null
    // Cleared before the round trip so a project switch never shows the previous
    // project's roster under a loading spinner (or as if it belonged to the new one).
    setMembers(null)
    setMembersLoading(true)
    setMembersError(null)
    void fetchBacklogMembers(requestedKey)
      .then((page) => {
        if (cancelled) return
        // Upstream echoes the project it answered for. A mismatched key on the
        // *current* request (not a cancelled prior pick) must surface as an
        // error so Retry is offered — silent drop left Save gated forever.
        const echoedKey = page.project_key?.trim() || null
        if (requestedKey && echoedKey && echoedKey !== requestedKey) {
          setMembers(null)
          setMembersError(getServiceStrings().membersLoadError)
          return
        }
        setMembers(page.members)
        setMembersError(null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setMembers(null)
        setMembersError(err instanceof Error ? err.message : null)
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [rosterActive, connected, canAssign, projectKey, membersReloadToken])

  useEffect(() => {
    // Dry-run reads the STORED meeting on the server. An unsaved Project pick
    // must still load members/meta, but must not check against the wrong key.
    if (!config || !active || !connected || !checkEnabled) return undefined
    if (confirmedItemsKeyRef.current === key) {
      confirmedItemsKeyRef.current = null
      return undefined
    }
    let cancelled = false
    setPhase('checking')
    setError(null)
    // Verdicts belong to the rows they were computed for; an edit leaves the
    // old ones describing items that are no longer on screen. The ticks go with
    // them: they are positions, and an inserted or deleted item moves whichever
    // rows sat after it.
    setRows(new Map())
    setOutcome(null)
    setSkipped(new Set())
    setCollapsedConfirm(new Set())
    setConfirmError(new Map())
    setConfirmNote(new Map())
    pendingConfirmedActionsRef.current.clear()
    void syncBacklogTasks(meetingId, { dryRun: true })
      .then((response) => {
        if (cancelled) return
        setRows(new Map(response.results.map((row) => [row.index, row])))
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : null)
      })
      .finally(() => {
        if (!cancelled) setPhase('idle')
      })
    return () => {
      cancelled = true
    }
  }, [config, active, connected, checkEnabled, key, meetingId])

  /**
   * Who each row points at, before the reviewer touches anything.
   *
   * A saved `assignee_id` wins: that is the reviewer picking from the project
   * roster. Otherwise the minutes may still carry an address the picker wrote,
   * and the Backlog member carries the same one — matching on it puts a real
   * person on the issue without asking them to re-pick people they already chose.
   */
  const fromMinutes = useMemo(() => {
    const rosterProject = projectKey?.trim() || null
    const writeProject = writeProjectKey?.trim() || null
    // Draft picker roster is not the write target — matching addresses against
    // it would send foreign member ids onto the stored project's issues.
    const rosterMatchesWriteTarget =
      !writeProject || !rosterProject || writeProject === rosterProject
    const byAddress = new Map(
      rosterMatchesWriteTarget
        ? (members ?? [])
            .filter((member) => member.mail_address)
            .map((member) => [member.mail_address!.trim().toLowerCase(), member.id])
        : [],
    )
    const resolved: Record<number, number | null> = {}
    actionItems.forEach((item, index) => {
      // Reviewer pick on the minutes wins over the dry-run guess below —
      // including an explicit `null` clear, which must not revive the dry-run
      // assignee. Saved ids still apply when the roster is for a different
      // draft project: Push reads the stored meeting.
      if (item.assignee_id !== undefined) {
        resolved[index] = item.assignee_id
        return
      }
      // Leave AI-resolved rows alone — email matching must not override them.
      if (rows.get(index)?.assignee_id != null) return
      const id = byAddress.get(parseDirectoryEmail(item.assignee ?? ''))
      if (id !== undefined) resolved[index] = id
    })
    return resolved
  }, [actionItems, members, rows, projectKey, writeProjectKey])

  const assigneeIds = useMemo(() => {
    // Dry-run first, minutes last: a saved assignee_id / explicit null clear
    // (or address match when the AI left the row unresolved) must travel on
    // the write override.
    const merged: Record<number, number | null> = {}
    for (const [index, row] of rows) {
      if (row.assignee_id != null) merged[Number(index)] = row.assignee_id
    }
    return { ...merged, ...fromMinutes }
  }, [fromMinutes, rows])

  const toggleSkip = useCallback((index: number) => {
    setSkipped((current) => {
      const next = new Set(current)
      if (!next.delete(index)) next.add(index)
      return next
    })
  }, [])

  const sync = useCallback(async () => {
    // Keyed by index so a row that is both unticked and reassigned travels as
    // one override rather than two, the second of which would drop the first.
    const byIndex = new Map<number, BacklogTaskOverride>()
    for (const [index, id] of Object.entries(assigneeIds)) {
      byIndex.set(Number(index), { index: Number(index), assignee_id: id })
    }
    for (const index of skipped) {
      byIndex.set(index, { ...(byIndex.get(index) ?? { index }), skip: true })
    }
    // Overwrites never travel with the batch. `POST /tasks` writes a confident
    // match itself and reports it `updated`, which would be a ticket
    // overwritten without anybody seeing what was on it — so they are held
    // back by the same mechanism as an unticked row and go out one at a time
    // from their own panel.
    for (const [index, row] of rows) {
      if (needsConfirmation(row)) {
        byIndex.set(index, { ...(byIndex.get(index) ?? { index }), skip: true })
      }
    }
    const overrides: BacklogTaskOverride[] = [...byIndex.values()]
    // Includes rows matched from the minutes, not just clicked ones — the
    // server could not resolve those names, so it needs to be told.
    setPhase('syncing')
    setError(null)
    try {
      const response = await syncBacklogTasks(meetingId, { dryRun: false, overrides })
      logActivity('minutes.pushed_to_backlog', { meetingId })
      setOutcome(response)
      // Merged, not replaced: a row held out of the batch has no result, and
      // replacing would drop the verdict that says it is an overwrite waiting
      // to be confirmed — taking its panel with it.
      setRows((current) => {
        const next = new Map(current)
        for (const row of response.results) next.set(row.index, row)
        return next
      })
    } catch (err) {
      logActivity('minutes.pushed_to_backlog', { meetingId, status: 'failure' })
      setError(err instanceof Error ? err.message : null)
    } finally {
      setPhase('idle')
    }
  }, [assigneeIds, meetingId, rows, skipped])

  const toggleConfirm = useCallback((index: number) => {
    setCollapsedConfirm((current) => {
      const next = new Set(current)
      if (!next.delete(index)) next.add(index)
      return next
    })
  }, [])

  /**
   * Write the overwrite the reviewer approved, and fold the result back into
   * the row.
   *
   * The route answers 200 whatever happens, so `status` is the only thing that
   * says whether anything was written — reading a resolved promise as success
   * would report a write that did not happen.
   *
   * - `updated` settles the row and the panel goes with it.
   * - `blocked` wrote nothing: the reply carries the ticket as it now stands,
   *   which is folded back into the row so the panel redraws against the
   *   values that refused it rather than the ones it was opened with. No
   *   message is recorded for it — the refreshed `conflicts[]` already carry
   *   the server's own sentence, and repeating it would print it twice.
   * - `noop` means everything already matched. Settled, but not written to —
   *   saying "Updated" would claim an edit Backlog never saw.
   * - `dry_run` is the server refusing to write at all; left as it was.
   */
  const applyConfirm = useCallback(
    async (index: number, payload: UpdateBacklogIssuePayload) => {
      const row = rows.get(index)
      const issueKey = row?.matched_issue_key ?? row?.issue_key
      if (!row || !issueKey) return
      setConfirmApplying((current) => new Set(current).add(index))
      setConfirmError((current) => {
        const next = new Map(current)
        next.delete(index)
        return next
      })
      try {
        let pending = pendingConfirmedActionsRef.current.get(index)
        if (!pending) {
          const result = await updateBacklogIssue(meetingId, issueKey, payload)
          if (result.status === 'updated' || result.status === 'noop') {
            const item = actionItems[index]
            if (!item) return
            pending = {
              item: confirmedActionItem(item, row, payload, members),
              result,
            }
            pendingConfirmedActionsRef.current.set(index, pending)
          } else {
            // Still `matched`: nothing was written, so the row keeps asking.
            setRows((current) => {
              const next = new Map(current)
              const existing = next.get(index)
              if (!existing) return current
              next.set(index, {
                ...existing,
                current_summary: result.current_summary,
                current_priority: result.current_priority,
                current_description: result.current_description,
                current_deadline: result.current_deadline,
                current_assignee_id: result.current_assignee_id,
                current_assignee_name: result.current_assignee_name,
                current_updated: result.current_updated,
                current_is_fresh: true,
                conflicts: result.conflicts,
              })
              return next
            })
            return
          }
        }

        if (onConfirmedActionPersist) {
          confirmedItemsKeyRef.current = itemsKey(
            actionItems.map((item, itemIndex) => (itemIndex === index ? pending.item : item)),
          )
          await onConfirmedActionPersist(index, pending.item)
        }
        pendingConfirmedActionsRef.current.delete(index)
        const { result } = pending
        setRows((current) => {
          const next = new Map(current)
          const existing = next.get(index)
          if (!existing) return current
          if (result.status === 'updated') {
            next.set(index, {
              ...existing,
              status: 'updated',
              issue_key: result.issue_key,
              url: result.url,
            })
          }
          return next
        })
        if (result.status === 'noop') {
          setConfirmNote((current) => new Map(current).set(index, 'noop'))
          setCollapsedConfirm((current) => new Set(current).add(index))
        }
      } catch (err) {
        // Left open: the commonest refusal is that somebody edited the issue
        // since, and closing would take away the values the reviewer needs.
        setConfirmError((current) =>
          new Map(current).set(index, err instanceof Error ? err.message : ''),
        )
      } finally {
        setConfirmApplying((current) => {
          const next = new Set(current)
          next.delete(index)
          return next
        })
      }
    },
    [actionItems, meetingId, members, onConfirmedActionPersist, rows],
  )

  const dismiss = useCallback(() => {
    setOutcome(null)
    setError(null)
  }, [])

  return {
    config,
    members,
    membersLoading,
    membersError,
    reloadMembers,
    rows,
    assigneeIds,
    phase,
    error,
    outcome,
    skipped,
    toggleSkip,
    sync,
    dismiss,
    priorities: Object.keys(meta?.priorities ?? {}),
    issueTypes: Object.keys(meta?.issue_types ?? {}),
    creatableCount: actionItems.reduce((count, _item, index) => {
      // Three ways a row is not going to create anything: the reviewer
      // unticked it, it is an overwrite waiting on its own panel, or the
      // server already found the ticket it would have made.
      const row = rows.get(index)
      if (skipped.has(index) || needsConfirmation(row) || isAlreadyCreated(row)) return count
      return count + 1
    }, 0),
    collapsedConfirm,
    toggleConfirm,
    confirmError,
    confirmNote,
    confirmApplying,
    applyConfirm,
  }
}
